// safety/safety-walks/components/shared.tsx
import React, { useEffect, useState } from 'react';
import { Plus, Expand, Loader2, ImageOff } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../components/ui/dialog';
import { cn } from '../../../../components/ui/utils';
import { RISK_BADGE_CLASS, STATUS_BADGE_CLASS } from '../../../../types/safety-walk.types';
import type { ObservationStatus, PhotoMeta, RiskRating, SafetyObservation, SafetyWalkStatus } from '../../../../types/safety-walk.types';

/* ---------- Slide-over Drawer (mirrors the incidents/hazardous-chemicals drawers) ---------- */
export const Drawer: React.FC<{
    open: boolean;
    width?: string;
    onClose: () => void;
    children: React.ReactNode;
}> = ({ open, width = '640px', onClose, children }) => {
    const [mounted, setMounted] = useState(open);
    useEffect(() => {
        if (open) setMounted(true);
        else {
            const t = setTimeout(() => setMounted(false), 280);
            return () => clearTimeout(t);
        }
    }, [open]);

    if (!mounted) return null;
    return (
        <>
            <div
                onClick={onClose}
                className="fixed inset-0 z-30 bg-black/40 transition-opacity duration-300"
                style={{ opacity: open ? 1 : 0 }}
            />
            <div
                className="fixed top-0 right-0 h-screen z-40 bg-card shadow-2xl flex flex-col transition-transform duration-300"
                style={{ width, transform: open ? 'translateX(0)' : 'translateX(100%)' }}
            >
                {children}
            </div>
        </>
    );
};

/* ---------- Status pill ---------- */
export const StatusPill: React.FC<{ status: ObservationStatus | SafetyWalkStatus }> = ({ status }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_BADGE_CLASS[status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
        {status}
    </span>
);

/* ---------- Risk badge ---------- */
export const RiskBadge: React.FC<{ risk: RiskRating | string | null | undefined }> = ({ risk }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${RISK_BADGE_CLASS[(risk as RiskRating) || 'Low'] || RISK_BADGE_CLASS.Low}`}>
        {risk || 'Low'}
    </span>
);

/* ---------- Overdue / due-soon indicator ---------- */
export const DueIndicator: React.FC<{ isOverdue?: boolean; dueDate?: string | null }> = ({ isOverdue, dueDate }) => {
    if (!dueDate) return null;
    if (isOverdue) {
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-red-50 text-red-700 border-red-200">OVERDUE</span>;
    }
    const today = new Date().toISOString().slice(0, 10);
    if (dueDate <= today) return null;
    const days = Math.ceil((new Date(`${dueDate}T00:00:00`).getTime() - new Date(`${today}T00:00:00`).getTime()) / 86400000);
    if (days <= 7) {
        return <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-amber-50 text-amber-700 border-amber-200">DUE SOON</span>;
    }
    return null;
};

/* ---------- Info cell (read-only labelled value) ---------- */
export const InfoCell: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="rounded-lg border border-border p-3">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">{label}</div>
        <div className="text-sm font-medium">{children ?? '—'}</div>
    </div>
);

/* ---------- Section title with optional right action ---------- */
export const SectionTitle: React.FC<{ title: string; right?: React.ReactNode }> = ({ title, right }) => (
    <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold">{title}</h3>
        {right}
    </div>
);

/* ---------- Primary "Add X" button ---------- */
export const AddButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
    <button
        onClick={onClick}
        className="inline-flex items-center gap-1.5 h-9 px-3.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90"
    >
        <Plus size={16} />
        {label}
    </button>
);

/* ---------- Date + initials helpers ---------- */
export const fmtDate = (d?: string | null): string => {
    if (!d) return '—';
    const date = new Date(`${d.slice(0, 10)}T00:00:00`);
    if (isNaN(date.getTime())) return d;
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

/* ---------- Date + time ("11 Jun 2025 09:22") ---------- */
export const fmtDateTime = (d?: string | null): string => {
    if (!d) return '—';
    const date = new Date(d);
    if (isNaN(date.getTime())) return d;
    return date
        .toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })
        .replace(',', '');
};

/* ---------- Close-out submission meta (who submitted + when) ----------
   Sourced from the audit trail — submitCloseOut() prepends a
   'Close-Out Submitted' entry; falls back to the responsible person. */
export const closeOutSubmission = (obs: SafetyObservation): { who: string; when: string | null } => {
    const entry = (obs.audit || []).find((a) => a.action === 'Close-Out Submitted');
    return {
        who: entry?.who || obs.personResponsibleName || '—',
        when: entry?.date || null,
    };
};

export const initials = (name?: string | null): string =>
    (name || '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join('') || '?';

/* ---------- Evidence photo thumbnail with "View" lightbox CTA ----------
   The badge is always visible on touch devices and hover-revealed on sm+.
   Pass className="border-0 rounded-none" when embedding inside a bordered card. */
export const PhotoThumb: React.FC<{ photo: PhotoMeta; onView: (photo: PhotoMeta) => void; className?: string }> = ({ photo, onView, className }) => (
    <button
        type="button"
        onClick={() => onView(photo)}
        className={cn(
            'relative group block w-full rounded-lg border border-border overflow-hidden bg-muted/30 focus:outline-none focus:ring-2 focus:ring-primary/30',
            className,
        )}
        title={`View ${photo.filename}`}
    >
        <img src={photo.url} alt={photo.filename} className="h-40 w-full object-cover" />
        <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 rounded-md bg-black/60 px-2 py-1 text-[11px] font-medium text-white sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
            <Expand size={12} /> View
        </span>
    </button>
);

/* ---------- Full-resolution photo lightbox ----------
   The dialog hugs the photo's natural resolution and only scales down
   (never up) when the photo would overflow the viewport. The cap is
   derived from window.innerWidth/innerHeight, so it also fits mobile. */
export const PhotoLightbox: React.FC<{ photo: PhotoMeta | null; onClose: () => void }> = ({ photo, onClose }) => {
    const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [viewport, setViewport] = useState({ w: window.innerWidth, h: window.innerHeight });

    // Reset dimensions whenever a different photo is opened — this component
    // stays mounted between previews, so stale sizes would otherwise linger.
    const url = photo?.url;
    useEffect(() => {
        setNaturalSize(null);
        setLoadFailed(false);
    }, [url]);

    useEffect(() => {
        if (!photo) return;
        const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [photo]);

    // Chrome around the image: p-4 padding (32px per axis), title row (~20px)
    // + gap-3 (12px), plus a 16px viewport margin on every side.
    const maxImgW = Math.max(viewport.w - 64, 160);
    const maxImgH = Math.max(viewport.h - 96, 160);

    let imgW: number | null = null;
    let imgH: number | null = null;
    if (naturalSize) {
        const scale = Math.min(1, maxImgW / naturalSize.w, maxImgH / naturalSize.h);
        imgW = Math.round(naturalSize.w * scale);
        imgH = Math.round(naturalSize.h * scale);
    }

    return (
        <Dialog open={!!photo} onOpenChange={(open) => { if (!open) onClose(); }}>
            <DialogContent
                className="w-auto sm:max-w-none p-4 gap-3"
                style={{ width: imgW ? imgW + 32 : 'min(452px, calc(100vw - 2rem))' }}
            >
                <DialogHeader>
                    <DialogTitle className="text-sm font-medium truncate pr-6">{photo?.filename || 'Photo'}</DialogTitle>
                </DialogHeader>
                {photo && (
                    <div
                        className="relative flex items-center justify-center rounded-md bg-muted/30 overflow-hidden"
                        style={imgW && imgH ? { width: imgW, height: imgH } : { width: '100%', height: 280 }}
                    >
                        {!naturalSize && !loadFailed && (
                            <Loader2 size={24} className="absolute animate-spin text-muted-foreground" />
                        )}
                        {loadFailed ? (
                            <div className="flex flex-col items-center gap-2 text-muted-foreground">
                                <ImageOff size={24} />
                                <span className="text-xs">Could not load photo</span>
                            </div>
                        ) : (
                            <img
                                src={photo.url}
                                alt={photo.filename}
                                onLoad={(e) => setNaturalSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
                                onError={() => setLoadFailed(true)}
                                className="h-full w-full object-contain"
                            />
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
